// ./src/styles/theme/colors.ts
import { MantineColorsTuple } from "@mantine/core";
import type { ThemeVariant } from "./variants";

// ============================================
// Eventsmagic Color System — "Sunset Soirée"
// Warm, celebratory, premium. A coral primary and a magic-night
// violet secondary, lifted by marigold gold and jade teal, all set
// on warm plum-tinted neutrals. Deliberately avoids the cold
// blue/indigo SaaS cliché.
// ============================================

// ── Coral / Persimmon — primary brand (warm, energetic, celebratory)
export const coral: MantineColorsTuple = [
  "#fff1ec", // 0
  "#ffe0d5", // 1
  "#ffbfa9", // 2
  "#ff9c7b", // 3
  "#fb7a53", // 4
  "#f65f38", // 5
  "#bd3d1d", // 6 — primary filled (white 5.5:1; was #e84e28, 3.8:1 — #66, owner 2026-10-06)
  "#a8361b", // 7
  "#9f331a", // 8
  "#7c2916", // 9
];

// ── Grape / Violet — secondary (twilight, magic, depth)
export const grape: MantineColorsTuple = [
  "#f4f0ff", // 0
  "#e8e0ff", // 1
  "#cfc0fb", // 2
  "#b29bf6", // 3
  "#9775ef", // 4
  "#7e57e6", // 5
  "#6b42d4", // 6 — main
  "#5a34b4", // 7
  "#472a8c", // 8
  "#35216b", // 9
];

// ── Gold / Marigold — accent, celebration, warning
export const gold: MantineColorsTuple = [
  "#fff8e1", // 0
  "#ffeeb3", // 1
  "#fbdd7c", // 2
  "#f7c948", // 3
  "#f5bd30", // 4
  "#f0b429", // 5 — main
  "#e8a80c", // 6 — filled: BRIGHT, with dark text (10:1). A LIGHT HUE: its light / outline / subtle text is shade 8
  "#c78400", // 7 — hover
  "#6a450f", // 8 — text on its own wash (7.9:1) — see `LIGHT_HUES` in ./index.ts
  "#573912", // 9
];

// ── Jade / Teal — info, cool balance
export const jade: MantineColorsTuple = [
  "#e4fbf4", // 0
  "#c0f3e6", // 1
  "#8ce7d3", // 2
  "#4fd6bc", // 3
  "#1fbfa3", // 4
  "#0da88c", // 5 — main
  "#077a66", // 6 (AA: white 5.3:1, light-badge text 4.6:1; was #0a8872)
  "#0c6c5b", // 7
  "#0d564a", // 8
  "#0b453d", // 9
];

// ── Emerald — success / positive
export const emerald: MantineColorsTuple = [
  "#e7fbef", // 0
  "#c6f3d8", // 1
  "#93e7b7", // 2
  "#56d690", // 3
  "#24c06d", // 4
  "#0fa557", // 5 — main
  "#087a42", // 6 (AA: white 5.4:1, light-badge text 4.7:1; was #0a8749)
  "#0b6c3c", // 7
  "#0c5632", // 8
  "#0a452a", // 9
];

// ── Crimson — error / destructive (cool red, distinct from coral)
export const crimson: MantineColorsTuple = [
  "#ffecee", // 0
  "#ffd2d9", // 1
  "#fba6b4", // 2
  "#f5738a", // 3
  "#ec4867", // 4
  "#de2a50", // 5 — main
  "#c21d42", // 6
  "#9e1837", // 7
  "#7d162e", // 8
  "#5f1425", // 9
];

// ── Amber — warm orange (distinct from coral & gold)
export const amber: MantineColorsTuple = [
  "#fff0e4", // 0
  "#ffdcc0", // 1
  "#febd8a", // 2
  "#fb9e54", // 3
  "#f5842b", // 4
  "#e86f14", // 5 — main
  "#b04d06", // 6 (AA: white 5.4:1, light-badge text 4.7:1; was #c85808)
  "#a2470b", // 7
  "#7f390f", // 8
  "#64300f", // 9
];

// ── Periwinkle — a harmonizing blue-violet (fills the "blue" slot)
export const periwinkle: MantineColorsTuple = [
  "#eef1ff", // 0
  "#dbe1ff", // 1
  "#b9c3fb", // 2
  "#93a2f6", // 3
  "#7183ef", // 4
  "#586ce6", // 5
  "#4756d4", // 6 — main
  "#3b45b0", // 7
  "#30388a", // 8
  "#272e6b", // 9
];

// ── Ink — warm plum-tinted neutral (the "greige" that makes it bespoke)
export const ink: MantineColorsTuple = [
  "#f7f5f6", // 0 — faint warm off-white
  "#efeced", // 1
  "#e2dde1", // 2 — borders
  "#cfc8ce", // 3 — disabled
  "#ada3ac", // 4 — placeholder
  "#877c88", // 5 — secondary text
  "#6a5f6c", // 6
  "#4f4551", // 7 — body text
  "#372e3a", // 8 — headings
  "#221b26", // 9 — near-black plum
];

// ── Dark — deep plum-ink surfaces for dark mode
export const plumDark: MantineColorsTuple = [
  "#c9c3ce", // 0
  "#a79fad", // 1
  "#857b8c", // 2
  "#6b6172", // 3
  "#4c4353", // 4
  "#3a3140", // 5
  "#2a2230", // 6 — surface
  "#201927", // 7 — body bg
  "#17111c", // 8
  "#0f0b13", // 9
];

// ============================================
// The BUSINESS variant's own hues (ROADMAP #66, owner 2026-09-29: "PartyLabz stays orange-ish; eventsmagic becomes
// purple-ish", shades from the eventsmagic logo — #46228F purple, #00BCBC teal).
// ⚠ Every shade 6 — here AND in the shared palettes above, which both variants use — carries its text at WCAG AA
//   (≥ 4.5:1): filled, and as the light variant's text on its own 10% wash. Every business category hue sits ≥ 40° away
//   from the primary, so a category chip never reads as a primary button. `check-brand-theme` (apps/app) measures
//   both brands; change a hex and run it.
// ============================================

// ── Iris — the business primary, from the logo's purple (shade 8 IS the logo).
export const iris: MantineColorsTuple = [
  "#f3effc", // 0
  "#e6ddf9", // 1
  "#cbb9f2", // 2
  "#ae93ea", // 3
  "#9372e2", // 4
  "#7a55d8", // 5 — dark-mode primary
  "#6339c6", // 6 — primary filled (white 7.2:1)
  "#5329a8", // 7 — hover
  "#46228f", // 8 — the logo
  "#331968", // 9
];

// ── Orchid — the business variant's `grape` / `violet`: those names are CATEGORIES (Receive, scheduled, private,
//    headliner…), and with a purple primary a purple category would read as a primary control.
export const orchid: MantineColorsTuple = [
  "#fdf0fa", // 0
  "#f9dcf2", // 1
  "#f2b8e4", // 2
  "#e98fd3", // 3
  "#de68c1", // 4
  "#cf47ad", // 5
  "#b32f95", // 6 — main
  "#93267a", // 7
  "#741f61", // 8
  "#5a194c", // 9
];

// ── Azure — the business variant's `blue` / `indigo`: periwinkle sits 24° from the purple primary, too close.
export const azure: MantineColorsTuple = [
  "#e8f4fd", // 0
  "#cde6fa", // 1
  "#9dcdf4", // 2
  "#69b1ec", // 3
  "#3d97e2", // 4
  "#1f80d4", // 5
  "#156cb8", // 6 — main
  "#135a99", // 7
  "#134a7b", // 8
  "#123c62", // 9
];

// ── Lagoon — the business secondary / info teal, from the logo's teal (shade 4 ≈ the logo).
export const lagoon: MantineColorsTuple = [
  "#e0f8f7", // 0
  "#bdf0ee", // 1
  "#85e3df", // 2
  "#45d1cc", // 3
  "#12c0bc", // 4 — ≈ the logo
  "#00a19f", // 5 — main
  "#007877", // 6 — filled (white 5.3:1)
  "#05666a", // 7
  "#0c5a5b", // 8
  "#0d4849", // 9
];

// ── Lime — a LIGHT HUE like gold (bright filled, dark text; shade 8 for text on its wash). Its own slot on the wheel,
//    between gold and green, so a status that needs one more distinct colour (RSVP "waitlisted") has one.
export const lime: MantineColorsTuple = [
  "#f3f9e3", // 0
  "#e3f1bf", // 1
  "#cbe48b", // 2
  "#b0d457", // 3
  "#a6d24a", // 4
  "#96c935", // 5
  "#82b81a", // 6 — filled, dark text (8.8:1)
  "#6a9a12", // 7 — hover
  "#46620b", // 8 — text on its own wash (6.4:1)
  "#36490c", // 9
];

// ── Slate — cool neutral faintly tinted toward the primary (a B2B tool reads calmer on cool greys than on warm ones).
export const slate: MantineColorsTuple = [
  "#f6f6f9", // 0
  "#ececf2", // 1
  "#dddce6", // 2 — borders
  "#c8c6d4", // 3 — disabled
  "#a6a3b6", // 4 — placeholder
  "#757289", // 5 — secondary text (4.6:1 on white)
  "#615e75", // 6 — dimmed
  "#4a475d", // 7 — body text
  "#333046", // 8 — headings
  "#1f1c30", // 9 — near-black
];

// ============================================
// Legacy-name aliases (kept so nothing that imports the old
// names breaks — all now point at the new palette).
// ============================================
export const brand = coral;
export const accent = gold;
export const road = grape;
export const fuel = jade;
export const success = emerald;
export const error = crimson;
export const warning = amber;
export const neutral = ink;

// ============================================
// Semantic Color Aliases
// ============================================
export const semanticColors = {
  paid: emerald[5],
  pending: gold[5],
  overdue: crimson[5],
  scheduled: grape[5],
  info: jade[5],
  link: coral[6],
  linkHover: coral[7],
  highlight: gold[3],
} as const;

// ============================================
// Surfaces / Backgrounds
// ============================================
export const backgrounds = {
  primary: "#ffffff",
  app: "#faf6f2", // warm alabaster — the app canvas
  secondary: ink[0],
  tertiary: ink[1],
  brand: coral[0],
  brandDark: grape[9],
  accent: gold[0],
  night: "#1a1420", // deep plum for hero / immersive sections
} as const;

// ============================================
// Text Colors
// ============================================
export const textColors = {
  primary: ink[8],
  secondary: ink[6],
  dimmed: ink[5],
  inverse: "#fff7f2",
  brand: coral[7],
  accent: gold[7],
  muted: ink[4],
} as const;

// ============================================
// Border Colors
// ============================================
export const borderColors = {
  light: ink[2],
  medium: ink[3],
  dark: ink[4],
  brand: coral[2],
  accent: gold[2],
  subtle: ink[1],
} as const;

// ============================================
// Gradients — festive, confetti-inspired
// ============================================
export const gradients = {
  // Sunset — the signature brand gradient
  primary: "linear-gradient(135deg, #e84e28 0%, #f0b429 100%)",

  // Aurora — magic-night hero (violet → coral → gold)
  hero: "linear-gradient(120deg, #472a8c 0%, #e84e28 60%, #f0b429 100%)",

  // Twilight — immersive dark sections
  night: "linear-gradient(160deg, #221b26 0%, #35216b 100%)",

  // Jade energy
  cool: "linear-gradient(135deg, #0c6c5b 0%, #4fd6bc 100%)",

  // Marigold spotlight
  accent: "linear-gradient(135deg, #c78400 0%, #f7c948 100%)",

  // Grape → coral (confetti pop)
  festive: "linear-gradient(135deg, #6b42d4 0%, #f65f38 100%)",

  // Subtle warm wash
  subtle: "linear-gradient(180deg, #fff1ec 0%, #faf6f2 100%)",

  // Premium dark
  dark: "linear-gradient(135deg, #0f0b13 0%, #35216b 100%)",
} as const;

// ============================================
// Categorical palette (charts, tags, avatars)
// ============================================
export const chartColors = {
  primary: [
    "#e84e28", // coral
    "#6b42d4", // grape
    "#e0a008", // gold
    "#0da88c", // jade
    "#4756d4", // periwinkle
    "#de2a50", // crimson
    "#0fa557", // emerald
    "#877c88", // ink
  ],
  sequential: [
    "#fff1ec",
    "#ffe0d5",
    "#ffbfa9",
    "#ff9c7b",
    "#fb7a53",
    "#f65f38",
    "#e84e28",
    "#c63f1e",
    "#9f331a",
    "#7c2916",
  ],
} as const;

// Kept for backward-compat with any lingering imports.
export const expenseColors = {
  other: ink[4],
} as const;

// ============================================
// Surfaces, text, borders, gradients and charts — PER VARIANT. The exports above are the festive set (kept for
// existing imports); the theme factory reads `brandSurfaces(variant)`.
// ============================================
export interface BrandSurfaces {
  backgrounds: Record<keyof typeof backgrounds, string>;
  text: Record<keyof typeof textColors, string>;
  borders: Record<keyof typeof borderColors, string>;
  gradients: Record<keyof typeof gradients, string>;
  charts: { primary: readonly string[]; sequential: readonly string[] };
  semantic: Record<keyof typeof semanticColors, string>;
  /** "r g b" of the shadow ink — shadows are tinted with the neutral, never pure black. */
  shadowInk: string;
}

const festiveSurfaces: BrandSurfaces = {
  backgrounds,
  text: textColors,
  borders: borderColors,
  gradients,
  charts: chartColors,
  semantic: semanticColors,
  shadowInk: "34 27 38",
};

const businessSurfaces: BrandSurfaces = {
  backgrounds: {
    primary: "#ffffff",
    app: "#f6f5fa", // cool lavender-grey canvas — white cards lift off it (1.09:1), text stays AA on both
    secondary: slate[0],
    tertiary: slate[1],
    brand: iris[0],
    brandDark: iris[9],
    accent: lagoon[0],
    night: "#16132a",
  },
  text: {
    primary: slate[8],
    secondary: slate[6],
    dimmed: slate[5],
    inverse: "#ffffff",
    brand: iris[7],
    accent: lagoon[7],
    muted: slate[4],
  },
  borders: {
    light: slate[2],
    medium: slate[3],
    dark: slate[4],
    brand: iris[2],
    accent: lagoon[2],
    subtle: slate[1],
  },
  gradients: {
    primary: `linear-gradient(135deg, ${iris[7]} 0%, ${lagoon[5]} 100%)`,
    hero: `linear-gradient(120deg, ${iris[9]} 0%, ${iris[6]} 55%, ${lagoon[4]} 100%)`,
    night: `linear-gradient(160deg, ${slate[9]} 0%, ${iris[9]} 100%)`,
    cool: `linear-gradient(135deg, ${lagoon[7]} 0%, ${lagoon[3]} 100%)`,
    accent: gradients.accent,
    festive: `linear-gradient(135deg, ${iris[6]} 0%, ${orchid[5]} 100%)`,
    subtle: `linear-gradient(180deg, ${iris[0]} 0%, #f6f5fa 100%)`,
    dark: `linear-gradient(135deg, #0f0d1a 0%, ${iris[9]} 100%)`,
  },
  charts: {
    primary: [iris[6], lagoon[5], gold[5], orchid[5], azure[5], crimson[5], emerald[5], slate[5]],
    sequential: [...iris],
  },
  semantic: {
    paid: emerald[5],
    pending: gold[5],
    overdue: crimson[5],
    scheduled: orchid[5],
    info: lagoon[5],
    link: iris[6],
    linkHover: iris[7],
    highlight: gold[3],
  },
  shadowInk: "31 28 48",
};

/** The surfaces of a theme variant: festive (PartyLabz) keeps "Sunset Soirée"; business (eventsmagic) is iris + lagoon. */
export const brandSurfaces = (variant: ThemeVariant): BrandSurfaces =>
  variant === "business" ? businessSurfaces : festiveSurfaces;

// ============================================
// Color Palette Export
// ============================================
export const colorPalette = {
  coral,
  lime,
  iris,
  orchid,
  azure,
  lagoon,
  slate,
  grape,
  gold,
  jade,
  emerald,
  crimson,
  amber,
  periwinkle,
  ink,
  semantic: semanticColors,
  backgrounds,
  text: textColors,
  borders: borderColors,
  gradients,
  charts: chartColors,
} as const;
